import { FC } from 'react';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import { Accordion, Badge, SimpleGrid, Text } from '@mantine/core';
import { getFileSrc } from '@/utils/utilities';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AccordionOptionCard } from '@/components/ui/AccordionOptionCard';
import { creatorActions } from '@/store/slices/creatorSlice';
import { UUID } from '@/types/types';
import ProductApi from '@/api/ProductApi';
import { useContent } from '@/providers/ContentProvider';

const baseSlug = 'creator.';

export const Products: FC = () => {
	const dispatch = useTypedDispatch();
	const { getContent } = useContent();

	const { product } = useTypedSelector((state) => state.creator);

	const productsQuery = useQuery({
		queryKey: [ProductApi.queryKey],
		queryFn: ProductApi.getAll,
	});

	const initializeMutation = useMutation({
		mutationFn: ProductApi.initialize,
		onSuccess: (data) => {
			dispatch(creatorActions.setProduct(data));
		},
	});

	const handleSelectProduct = (productId: UUID) => () => {
		initializeMutation.mutate(productId);
	};

	return (
		<Accordion.Item value={ProductApi.queryKey}>
			<Accordion.Control
				icon={
					!!product && (
						<Badge>
							<Text size="sm" fw="bold">
								{product.name}
							</Text>
						</Badge>
					)
				}
			>
				{getContent(`${baseSlug}firstOption.name`)}
			</Accordion.Control>
			<Accordion.Panel>
				<SimpleGrid
					cols={{
						base: 2,
						sm: 3,
					}}
					spacing="xs"
				>
					{productsQuery.data?.map((prod) => {
						const isSelected = product?.id === prod.id;

						return (
							<AccordionOptionCard
								key={prod.id}
								name={prod.name}
								description={prod.description}
								imagePath={getFileSrc(prod?.image?.path)}
								isSelected={isSelected}
								onSelect={handleSelectProduct(prod.id)}
							/>
						);
					})}
				</SimpleGrid>
			</Accordion.Panel>
		</Accordion.Item>
	);
};
